import { get } from './client'

// ── BI Report types (mirrors engine modules/reports/types.ts) ─────────────────

export type BiReportFilter = {
  period?:             string
  dateFrom?:           string
  dateTo?:             string
  source?:             string[]
  campaign?:           string[]
  city?:               string[]
  governorate?:        string[]
  orderType?:          string[]
  deliveryStatus?:     string[]
  shippingCompany?:    string[]
  cancellationReason?: string[]
  returnReason?:       string[]
  accountingSegment?:  string[]
  paymentMethod?:      string[]
  sku?:                string[]
  carrier?:            string[]
  agent?:              string[]
  team?:               string[]
  outcome?:            string[]
}

export type BiTrendPoint = { date: string; value: number }

export type BiTrendSeries = {
  id:      string
  label:   string
  unit:    string
  color?:  string
  points:  BiTrendPoint[]
  total:   number
  avg:     number
  change:  number
}

export type BiBreakdownRow = {
  key:       string
  label:     string
  count:     number
  pct:       number
  revenue?:  number
  avgValue?: number
  extra?:    Record<string, number | string>
}

export type BiBreakdownSection = {
  id:        string
  title:     string
  dimension: string
  unit?:     string
  rows:      BiBreakdownRow[]
}

export type BiTimingMetric = {
  id:                  string
  label:               string
  module:              string
  unit:                'seconds' | 'minutes' | 'hours' | 'days'
  sampleCount:         number
  avg:                 number | null
  median:              number | null
  min:                 number | null
  max:                 number | null
  p90:                 number | null
  p95:                 number | null
  slaThreshold?:       number
  slaAchievementPct?:  number
}

export type BiKpiRow = {
  kpiId:          string
  name:           string
  value:          number | null
  formattedValue: string
  target:         number | null
  achievementPct: number | null
  status:         string
  unit:           string
}

export type BiReport = {
  reportId:    string
  period:      string
  dateFrom:    string
  dateTo:      string
  filters:     BiReportFilter
  summary:     Record<string, number | null>
  trends:      BiTrendSeries[]
  breakdowns:  BiBreakdownSection[]
  timing:      BiTimingMetric[]
  kpis:        BiKpiRow[]
  meta:        Record<string, string | number>
  generatedAt: string
}

export type FilterOptions = {
  sources:            string[]
  cities:             string[]
  cancelReasons:      string[]
  agents:             string[]
  orderTypes:         string[]
  deliveryStatuses:   string[]
  carriers:           string[]
  accountingSegments: string[]
}

// ── Helper: build query string from filter ────────────────────────────────────

function filterToQuery(shop: string, filter: BiReportFilter): string {
  const params = new URLSearchParams({ shop })
  if (filter.period)              params.set('period', filter.period)
  if (filter.dateFrom)            params.set('dateFrom', filter.dateFrom)
  if (filter.dateTo)              params.set('dateTo', filter.dateTo)
  if (filter.source?.length)      params.set('source', filter.source.join(','))
  if (filter.campaign?.length)    params.set('campaign', filter.campaign.join(','))
  if (filter.city?.length)        params.set('city', filter.city.join(','))
  if (filter.governorate?.length) params.set('governorate', filter.governorate.join(','))
  if (filter.orderType?.length)   params.set('orderType', filter.orderType.join(','))
  if (filter.deliveryStatus?.length) params.set('deliveryStatus', filter.deliveryStatus.join(','))
  if (filter.shippingCompany?.length) params.set('shippingCompany', filter.shippingCompany.join(','))
  if (filter.cancellationReason?.length) params.set('cancellationReason', filter.cancellationReason.join(','))
  if (filter.accountingSegment?.length) params.set('accountingSegment', filter.accountingSegment.join(','))
  if (filter.paymentMethod?.length) params.set('paymentMethod', filter.paymentMethod.join(','))
  if (filter.sku?.length)         params.set('sku', filter.sku.join(','))
  if (filter.carrier?.length)     params.set('carrier', filter.carrier.join(','))
  if (filter.agent?.length)       params.set('agent', filter.agent.join(','))
  if (filter.team?.length)        params.set('team', filter.team.join(','))
  if (filter.outcome?.length)     params.set('outcome', filter.outcome.join(','))
  return params.toString()
}

// ── API client ────────────────────────────────────────────────────────────────

export const biReportsApi = {
  getOrders:              (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/orders?${filterToQuery(shop, filter)}`),

  getConfirmation:        (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/confirmation?${filterToQuery(shop, filter)}`),

  getSales:               (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/sales?${filterToQuery(shop, filter)}`),

  getShippingConfirmation: (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/shipping-confirmation?${filterToQuery(shop, filter)}`),

  getShipping:            (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/shipping?${filterToQuery(shop, filter)}`),

  getCustomerService:     (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/customer-service?${filterToQuery(shop, filter)}`),

  getWarehouse:           (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/warehouse?${filterToQuery(shop, filter)}`),

  getPurchaseOrders:      (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/purchase-orders?${filterToQuery(shop, filter)}`),

  getFinancial:           (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/financial?${filterToQuery(shop, filter)}`),

  getForecast:            (shop: string, filter: BiReportFilter = {}): Promise<BiReport> =>
    get<BiReport>(`/reports/forecast?${filterToQuery(shop, filter)}`),

  getFilterOptions:       (shop: string): Promise<FilterOptions> =>
    get<FilterOptions>(`/reports/filter-options?shop=${encodeURIComponent(shop)}`),
}
